import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { useAuthStore } from '../../context/auth'
import { Logo } from '../brand/Logo'
import { api } from '../../services/api'
import { USER_ROLE_LABELS } from '../../utils/format'
import { Icon, type IconName } from '../ui/icons'
import type { UserRole } from '../../types/api'

interface NavItem {
  to: string
  label: string
  icon: IconName
  roles: UserRole[]
}

const NAV_ITEMS: NavItem[] = [
  { to: '/app', label: 'Dashboard', icon: 'dashboard', roles: ['ADMIN', 'DOCTOR', 'RECEPTION', 'ACCOUNTANT'] },
  { to: '/app/agenda', label: 'Agenda', icon: 'agenda', roles: ['ADMIN', 'DOCTOR', 'RECEPTION'] },
  { to: '/app/ruta', label: 'Ruta del día', icon: 'route', roles: ['ADMIN', 'DOCTOR', 'RECEPTION'] },
  { to: '/app/controles', label: 'Próximos controles', icon: 'scheduleCheck', roles: ['ADMIN', 'DOCTOR', 'RECEPTION'] },
  { to: '/app/pacientes', label: 'Pacientes', icon: 'patients', roles: ['ADMIN', 'DOCTOR', 'RECEPTION', 'ACCOUNTANT'] },
  { to: '/app/plantillas', label: 'Mis plantillas', icon: 'checkup', roles: ['ADMIN', 'DOCTOR'] },
  { to: '/app/leads', label: 'Leads web', icon: 'leads', roles: ['ADMIN', 'RECEPTION'] },
  { to: '/app/cobros', label: 'Caja y cobros', icon: 'cashier', roles: ['ADMIN', 'RECEPTION', 'ACCOUNTANT'] },
  { to: '/app/preocupacional', label: 'Preocupacional', icon: 'preoccupational', roles: ['ADMIN', 'RECEPTION', 'ACCOUNTANT'] },
  { to: '/app/cotizaciones', label: 'Cotizaciones', icon: 'quotes', roles: ['ADMIN', 'RECEPTION', 'ACCOUNTANT'] },
  { to: '/app/contabilidad', label: 'Contabilidad', icon: 'accounting', roles: ['ADMIN', 'ACCOUNTANT'] },
  { to: '/app/produccion', label: 'Mi producción', icon: 'production', roles: ['ADMIN', 'ACCOUNTANT', 'DOCTOR'] },
  { to: '/app/honorarios', label: 'Honorarios', icon: 'fees', roles: ['ADMIN', 'ACCOUNTANT', 'DOCTOR'] },
  { to: '/app/blog', label: 'Blog', icon: 'blog', roles: ['ADMIN', 'DOCTOR'] },
  { to: '/app/mantenedores', label: 'Mantenedores', icon: 'settings', roles: ['ADMIN', 'ACCOUNTANT'] },
]

export function AppLayout() {
  const { user, refreshToken, logout } = useAuthStore()
  const navigate = useNavigate()

  async function handleLogout() {
    try {
      if (refreshToken) await api.post('/auth/logout', { refreshToken })
    } finally {
      logout()
      navigate('/login')
    }
  }

  const items = NAV_ITEMS.filter((item) => user && item.roles.includes(user.role))

  return (
    <div className="flex min-h-screen">
      <aside className="hidden w-56 shrink-0 flex-col bg-brand-900 text-white md:flex">
        <div className="px-5 py-6">
          <Logo variant="white" className="h-10" />
          <p className="mt-2 text-xs text-brand-200">Plataforma clínica</p>
        </div>
        <nav className="flex-1 space-y-0.5 px-3">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/app'}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors',
                  isActive ? 'bg-brand-600 font-semibold text-white' : 'text-brand-100 hover:bg-brand-700',
                )
              }
            >
              <Icon name={item.icon} className="h-[18px] w-[18px] shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-brand-700 p-4 text-xs">
          <p className="font-semibold">{user?.firstName} {user?.lastName}</p>
          <p className="mb-2 text-brand-200">{user ? USER_ROLE_LABELS[user.role] : ''}</p>
          <button onClick={handleLogout} className="rounded bg-brand-700 px-2.5 py-1.5 text-brand-100 hover:bg-brand-600">
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Barra superior móvil */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between bg-brand-900 px-4 py-3 text-white md:hidden">
          <Logo variant="white" className="h-7" />
          <button onClick={handleLogout} className="text-xs underline">Salir</button>
        </header>
        <nav className="flex gap-1 overflow-x-auto bg-brand-800 px-2 py-1.5 md:hidden">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/app'}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-1.5 whitespace-nowrap rounded px-2.5 py-1 text-xs',
                  isActive ? 'bg-brand-600 text-white' : 'text-brand-100',
                )
              }
            >
              <Icon name={item.icon} className="h-4 w-4 shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <main className="min-w-0 flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
